import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { Space } from '../types/venue';

type SpaceResponse = Space;
type DefaultError = unknown;

type UseSpaceParams = {
  spaceId?: string;
  /**
   * Comma-separated list of relations, e.g. 'venue'
   */
  include?: string;
  /** Override default enabled behavior */
  enabled?: boolean;
};

/**
 * useSpace
 *
 * @example
 * const spaceQuery = useSpace(
 *   {
 *     spaceId,
 *     include: 'venue',
 *   },
 *   {
 *     staleTime: 60_000,
 *   },
 * );
 */
export function useSpace<TData = SpaceResponse, TError = DefaultError>(
  { spaceId, include, enabled = true }: UseSpaceParams = {},
  options?: Omit<
    UseQueryOptions<SpaceResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const queryKey: QueryKey = ['space', spaceId, include];

  return useQuery<SpaceResponse, TError, TData>({
    queryKey,
    // don't run until we actually have an ID
    enabled: enabled && !!spaceId,
    queryFn: async () => {
      if (!spaceId) {
        throw new Error('spaceId is required to fetch a space');
      }

      return service.getSpace(spaceId, {
        ...(include ? { include } : {}),
      });
    },
    ...(options as any),
  });
}
